import React, { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../primitives';
import { Alert } from '../primitives';
import { SaveBarProps } from '../../context/SaveBarProvider';
import { useSaveBar } from '../../hooks/useSaveBar';
import { useLoadingBar } from '../../hooks/useLoadingBar';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';

type BreadcrumbItemProps = {
    url: string;
    current?: boolean;
    content?: string | React.ReactNode;
};

type AlertStatus = 'warning' | 'error' | 'info' | 'success';

/** Data shape for page-level alerts, mapped onto the primitives Alert. */
export interface IAlertProps {
    status?: AlertStatus;
    title?: string;
    message?: React.ReactNode;
}

const alertVariant: Record<AlertStatus, 'warning' | 'destructive' | 'info' | 'success'> = {
    warning: 'warning',
    error: 'destructive',
    info: 'info',
    success: 'success',
};

interface IPageProps {
    children?: React.ReactNode;
    title?: string | React.ReactNode;
    titleMeta?: React.ReactNode;
    actions?: React.ReactNode;
    subtitle?: string | React.ReactNode;
    alerts?: IAlertProps[];
    breadcrumbs?: BreadcrumbItemProps[] | React.ReactNode;
    fullWidth?: boolean;
    saveBar?: SaveBarProps;
    background?: 'white' | 'gray';
    loading?: boolean;
}

export const Page: React.FC<React.PropsWithChildren<IPageProps>> = ({
    title,
    subtitle,
    actions,
    breadcrumbs,
    children,
    fullWidth = false,
    loading = false,
    saveBar,
    alerts,
    titleMeta,
    background = 'gray',
}) => {
    const contextualSaveBar = useSaveBar();
    const loadingBar = useLoadingBar();

    // Consumers pass saveBar as an inline object, so its identity changes on
    // every render (each keystroke in the product form). Register against the
    // meaningful fields only and read the latest actions through a ref —
    // otherwise the context updates per keystroke and re-renders the whole
    // shell.
    const saveBarRef = useRef(saveBar);
    saveBarRef.current = saveBar;
    useEffect(() => {
        if (!saveBar) {
            return undefined;
        }
        contextualSaveBar.saveBar({
            show: saveBar.show,
            message: saveBar.message,
            messageStatus: saveBar.messageStatus,
            saveAction: {
                loading: saveBar.saveAction.loading,
                disabled: saveBar.saveAction.disabled,
                onAction: () => saveBarRef.current?.saveAction.onAction(),
            },
            discardAction: {
                loading: saveBar.discardAction.loading,
                disabled: saveBar.discardAction.disabled,
                onAction: () => saveBarRef.current?.discardAction.onAction(),
            },
        });
        return () => {
            contextualSaveBar.saveBar(undefined);
        };
    }, [
        !!saveBar,
        saveBar?.show,
        saveBar?.message,
        saveBar?.messageStatus,
        saveBar?.saveAction.loading,
        saveBar?.saveAction.disabled,
        saveBar?.discardAction.loading,
        saveBar?.discardAction.disabled,
    ]);

    useEffect(() => {
        if (loading) {
            loadingBar.show();
        } else {
            loadingBar.hide();
        }
        // Don't leave the global bar running if the page unmounts mid-load.
        return () => {
            loadingBar.hide();
        };
    }, [loading]);

    const renderBreadcrumbs = (breadcrumbs: BreadcrumbItemProps[] | React.ReactNode): React.ReactNode => {
        if (Array.isArray(breadcrumbs)) {
            // Router links (not raw <a href>) so breadcrumb navigation goes
            // through the SaveBarBlocker instead of hard-reloading past it.
            return (
                <Breadcrumb>
                    <BreadcrumbList>
                        {breadcrumbs.map((breadcrumb, index) => (
                            <React.Fragment key={index}>
                                {index > 0 && <BreadcrumbSeparator />}
                                <BreadcrumbItem>
                                    {breadcrumb.current ? (
                                        <BreadcrumbPage>{breadcrumb.content}</BreadcrumbPage>
                                    ) : (
                                        <BreadcrumbLink asChild>
                                            <RouterLink to={breadcrumb.url}>{breadcrumb.content}</RouterLink>
                                        </BreadcrumbLink>
                                    )}
                                </BreadcrumbItem>
                            </React.Fragment>
                        ))}
                    </BreadcrumbList>
                </Breadcrumb>
            );
        } else {
            return breadcrumbs;
        }
    };

    // One column width for the header, the alerts and the body, and semantic
    // tokens throughout — the page chrome was still on raw grey utilities, so
    // it ignored the shop's theme and sat on a different background to every
    // other screen.
    const column = fullWidth ? 'w-full px-6' : 'mx-auto w-full max-w-7xl px-6';

    return (
        <SaveBarBlocker>
            <div className={`min-h-screen ${background === 'white' ? 'bg-card' : 'bg-surface'}`}>
                {title && (
                    <div className={`${column} pt-6`}>
                        {breadcrumbs && renderBreadcrumbs(breadcrumbs)}
                        <div className="mt-1.5 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                            <div className="min-w-0">
                                {/* The state belongs beside the name, not on a
                                    line of its own: "Aurora Table Lamp ·
                                    changes pending review" is one fact. */}
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <h2
                                        data-testid="page-title"
                                        className="min-w-0 text-xl font-semibold tracking-tight text-foreground"
                                    >
                                        {title}
                                    </h2>
                                    {titleMeta}
                                </div>
                                {subtitle && (
                                    <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>
                                )}
                            </div>
                            {actions && <div className="flex items-center gap-2">{actions}</div>}
                        </div>
                    </div>
                )}
                {alerts && alerts.length > 0 && (
                    // Directly under the heading, and stacked with a gap:
                    // consecutive alerts used to butt together into one
                    // undifferentiated block.
                    <div className={`${column} mt-4 flex flex-col gap-2`}>
                        {alerts.map((alert, index) => (
                            <Alert
                                key={index}
                                variant={alertVariant[alert.status || 'info']}
                                title={alert.title}
                            >
                                {alert.message}
                            </Alert>
                        ))}
                    </div>
                )}
                <div className={`${column} py-5 pb-24`}>{children}</div>
            </div>
        </SaveBarBlocker>
    );
};
